// Batch 134 — hand-converted from src/legal/terms.md, which is the SOURCE OF
// RECORD. Edit the markdown first, then mirror it here. See PrivacyPolicy.jsx
// for what the correspondence test does and does not catch.
import LegalPage, { H2, P, B, UL, LI, Rule, Mail } from "./LegalPage";

export default function TermsOfService() {
  return (
    <LegalPage title="SparkCue Terms of Service" testId="legal-terms">
      <P>
        SparkCue is operated by an individual based in Ontario, Canada.
        Contact: <B><Mail /></B>
      </P>
      <P>By using SparkCue, you agree to these terms. If you do not, please do not use it.</P>
      <Rule />

      <H2>1. You must be 18</H2>
      <P>
        SparkCue is for adults. By using it you confirm you are 18 or older. If we learn you
        are not, we will close your account and delete your data.
      </P>
      <Rule />

      <H2>2. What SparkCue is</H2>
      <P>
        SparkCue reads a conversation you give it and suggests what to say next, or tells you
        to say nothing. It is a suggestion, not advice, and not a promise about how anyone
        will respond.
      </P>
      <P>
        <B>You decide what to send.</B> Everything SparkCue writes is a draft for you to
        judge. You are responsible for what you send and for how it lands.
      </P>
      <P>
        SparkCue uses AI, which means it can be wrong. It can misread a conversation, suggest
        something that does not fit, or state something about you it was never told. Read what
        it writes before you send it.
      </P>
      <P>
        <B>SparkCue is not a therapist, counsellor, doctor, lawyer, or emergency service.</B>{" "}
        Do not rely on it for emergencies, threats of violence, self-harm, abuse, or anything
        else that needs professional or emergency help. If you or someone else is in danger,
        contact your local emergency services.
      </P>
      <Rule />

      <H2>3. Your account</H2>
      <P>You sign in with an email address and a six-digit code we send you. There is no password.</P>
      <P>Keep access to that email address. Anyone who can read it can sign in as you.</P>
      <P>
        You may use SparkCue on a limited number of devices. If you reach the limit, you can
        sign a device out from the screen that refuses you, or ask us.
      </P>
      <P>One account per person. Do not share it.</P>
      <Rule />

      <H2>4. Free and paid</H2>
      <P><B>Free:</B> five replies. Not five a day — five in total.</P>
      <P><B>Paid:</B> unlimited replies, subject to reasonable use and anti-abuse protections.</P>
      <UL>
        <LI><B>$9.99 USD per month</B></LI>
        <LI><B>$59.99 USD per year</B></LI>
      </UL>
      <P>
        Prices are in US dollars and may change. If they do, we will tell you before your next
        renewal.
      </P>
      <P>
        <B>Subscriptions renew automatically until you cancel.</B> Cancel any time and you
        keep access until the end of the period you have paid for. Except where the law
        requires otherwise, we do not refund partial periods.
      </P>
      <P>
        Payments are handled by our payment processor. We never see or store your card details.
      </P>
      <P>
        Some accounts have different limits, set by us for testing or for people helping to
        build SparkCue.
      </P>
      <Rule />

      <H2>5. What you may not do</H2>
      <UL>
        <LI>Use SparkCue to harass, threaten, deceive, or manipulate anyone</LI>
        <LI>Use it to contact someone who has asked you to stop</LI>
        <LI>Use it on behalf of anyone under 18</LI>
        <LI>Use it to impersonate someone, commit fraud, or help anyone break the law</LI>
        <LI>Paste conversations you have no right to share</LI>
        <LI>Try to break, overload, or work around the limits</LI>
        <LI>Resell SparkCue or pass off its output as a product of your own</LI>
        <LI>Share your account</LI>
      </UL>
      <P>We may suspend or close an account that does any of this, without a refund.</P>
      <Rule />

      <H2>6. Your conversations are yours</H2>
      <P>You own what you paste. We do not claim it. It stays on your device.</P>
      <P>
        To the extent the law allows, you may use, modify and share what SparkCue writes for
        you. Send it, change it, ignore it.
      </P>
      <P>
        The app itself — the name, the design, the coaching engine, the writing in it —
        belongs to us.
      </P>
      <Rule />

      <H2>7. Feedback</H2>
      <P>
        If you tap Helpful or Not helpful, or type something into the feedback box, we may use
        it to improve SparkCue. We keep the reply you were reacting to. See the Privacy Policy.
      </P>
      <Rule />

      <H2>8. What we do not promise</H2>
      <P>
        SparkCue is provided as it is. We do not promise it will always be available, always
        be right, or produce any particular outcome in your life.
      </P>
      <P>
        We are not responsible for what happens in your relationships, your conversations, or
        anything you send. That is between you and the people you are texting.
      </P>
      <P>
        To the maximum extent the law allows, we are not liable for indirect, incidental,
        consequential or special damages, or for lost profits, data or goodwill. Our total
        liability arising from the service will not exceed what you paid us in the twelve
        months before the event giving rise to the claim. Nothing here limits liability that
        cannot lawfully be limited.
      </P>
      <P>Some places do not allow these limits. Where that is true, they do not apply to you.</P>
      <Rule />

      <H2>9. Ending it</H2>
      <P>
        <B>You</B> can stop at any time. Cancel your subscription yourself from your account
        settings in the app, and you keep access until the end of the period you have paid
        for. To delete your account entirely, email <B><Mail /></B> and we will action it.
      </P>
      <P>
        <B>We</B> may close your account if you break these terms, or if we stop running
        SparkCue. If we shut down, we will give reasonable notice and refund any unused paid
        period.
      </P>
      <Rule />

      <H2>10. Changes</H2>
      <P>
        We may change these terms. If a change matters, we will update the date above and tell
        you in the app. Continuing to use SparkCue after that means you accept the new terms.
      </P>
      <Rule />

      <H2>11. The whole agreement</H2>
      <P>
        These terms, the Privacy Policy, and anything shown to you at checkout are the
        agreement between you and SparkCue.
      </P>
      <P>If any part of them turns out to be unenforceable, the rest still stands.</P>
      <Rule />

      <H2>12. Law</H2>
      <P>
        These terms are governed by the laws of Ontario, Canada. Disputes go to the courts of
        Ontario.
      </P>
      <P>
        If you are a consumer somewhere with stronger protections, nothing here takes those
        away.
      </P>
      <Rule />

      <H2>13. Contact</H2>
      <P><B><Mail /></B></P>
    </LegalPage>
  );
}
